export const capitalizeFirstLetter = (string) => {
  if (!string) return;
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// function to read the file content
const readFileContent = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      resolve(event.target.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsText(file);
  });
};

// function to check the content of csv files
export const isCsvFileEmpty = async (file) => {
  try {
    const fileContent = await readFileContent(file);

    const rows = fileContent.split('\n');
    // here we are checking, if  the csv file has data other than it's header
    // we need at least header and 1 row of data to do csv imports
    if (rows.length <= 1 || (rows.length === 2 && rows[1].trim() === '')) {
      return true; // File is empty
    }
    return false; // File is not empty
  } catch {
    return true; // Error occurred while reading file
  }
};

export const swapArrayPositions = (arr, pos1, pos2) => {
  if (pos1 < 0 || pos1 >= arr.length || pos2 < 0 || pos2 >= arr.length) {
    throw new Error('Invalid index positions');
  }

  // Create a copy of the original array
  const newArr = [...arr];

  // Swap elements in the new array
  [newArr[pos1], newArr[pos2]] = [newArr[pos2], newArr[pos1]];

  return newArr;
};

// Normalize an offer doc filename to .docx. The API sometimes returns the
// legacy .doc extension (OLE2 format) even though the stored file is .docx
// (OOXML). Replacing .doc → .docx prevents the browser saving it as .doc.
export const toDocxFilename = (filename) => {
  if (!filename) return 'offer_document.docx';
  return filename.replace(/\.doc$/i, '.docx');
};

// `link.click()` only schedules the download, so the blob URL must outlive it —
// revoking on the next statement cancels large files mid-flight.
const BLOB_URL_TTL_MS = 60_000;

export const downloadFileFromUrl = async ({
  url: fileUrl,
  filename = 'file',
  onSuccess = undefined,
  onError = undefined,
  useDirect = false,
}) => {
  let objectUrl;

  try {
    const link = document.createElement('a');

    if (!useDirect) {
      // Blob download: Fetch and create blob URL
      const response = await fetch(fileUrl);
      if (!response.ok) {
        // Else an expired/denied URL downloads the error body as the file.
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }
      const blob = await response.blob();
      objectUrl = window.URL.createObjectURL(blob);
      fileUrl = objectUrl;
    }

    link.href = fileUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    onSuccess?.();
  } catch (error) {
    onError?.(error);
    console.error('Error downloading file:', error);
  } finally {
    if (objectUrl) {
      setTimeout(() => window.URL.revokeObjectURL(objectUrl), BLOB_URL_TTL_MS);
    }
  }
};

export const createBlobFromString = ({
  filename = '',
  content_type = '',
} = {}) => {
  if (!filename || !content_type) return null;
  const fileData = new Blob([], { type: content_type });
  const file = new File([fileData], filename, { type: content_type });
  return file;
};

export function nthForNumber(n) {
  return ['st', 'nd', 'rd'][((((n + 90) % 100) - 10) % 10) - 1] || 'th';
}

//function to extract invalid variable error message
export function getInvalidVariableError(error) {
  if (!error || typeof error !== 'string') {
    return 'Please ensure that the variables are valid';
  }
  const message = error.split(': ')[1]?.split('.').pop()?.trim() || error;
  return message;
}

export const checkForInvalidVariables = ({
  variablesListArr = [],
  content = '',
}) => {
  return (
    (
      content
        ?.match(/{{\s*(.*?)\s*}}/g)
        ?.filter(
          (v) => !variablesListArr.includes(v.replace(/{{\s*|\s*}}/g, ''))
        ) || []
    )?.length > 0
  );
};

export const checkForInvalidVariablesInSubjectAndBody = ({
  variablesListArr = [],
  subject = '',
  body = '',
  variablesListSubject = [],
}) => {
  const hasInvalidVariablesInBody = checkForInvalidVariables({
    variablesListArr,
    content: body,
  });
  const hasInvalidVariablesInSubject = checkForInvalidVariables({
    variablesListArr: variablesListSubject,
    content: subject,
  });

  const isInvalidVariablesFound =
    hasInvalidVariablesInBody || hasInvalidVariablesInSubject;

  return isInvalidVariablesFound;
};

export const checkForInvalidAttachments = ({ templateFormRef }) => {
  const currentTemplate = templateFormRef?.current?.values || {};

  const hasAttachments = currentTemplate.attachments?.length > 0;
  const hasInlineImages = /<img\s+[^>]*src=["'][^"']+["']/i.test(
    currentTemplate.body || ''
  );

  return {
    hasAttachments,
    hasInlineImages,
    inValid: hasAttachments || hasInlineImages,
  };
};

export const transformUserIdsToEmails = (userIds, userList) => {
  return userIds
    ?.map((id) => userList?.find((user) => user?.id === id))
    ?.map((user) => user?.email);
};

/**
 * Normalizes a given text string for comparison or search.
 *
 * Steps performed:
 * 1. Converts all characters to lowercase.
 * 2. Decomposes accented characters into base characters and diacritics.
 * 3. Removes diacritics (accents, tildes, etc.).
 * 4. Trims leading and trailing whitespace.
 *
 * @param {string} text - The input text to normalize.
 * @returns {string} - The normalized text.
 *
 * Example:
 * Input: "  Café au lait  "
 * Output: "cafe au lait"
 */
export const normalizeTextForSearch = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .toLowerCase()
    .normalize('NFD') // Decompose combined characters into base + diacritics
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .trim();
};

export const normalizeFiltersForApplications = (filters) => {
  const normalized = {};

  Object.keys(filters)
    .sort()
    .forEach((key) => {
      const value = filters[key];

      if (value === null || value === undefined) return;

      // Array normalization
      if (Array.isArray(value)) {
        normalized[key] = [...value].sort();
        return;
      }

      // Date range normalization
      if (typeof value === 'object' && ('gte' in value || 'lte' in value)) {
        normalized[key] = {
          gte: value.gte ?? null,
          lte: value.lte ?? null,
        };
        return;
      }

      normalized[key] = value;
    });

  return normalized;
};
